/**
 * Embed Token Manager
 * 
 * Stores and manages access tokens for iframe integration.
 * Flow: Parent sends access_token via postMessage → store and use directly
 */

// Token state
let accessToken = null;
let refreshToken = null;
let projectUuid = null;
let tokenExpiresAt = null;

/**
 * Store access token received from parent via postMessage
 * @param {object} data - Token data from parent
 */
export const storeAccessToken = (data) => {
    accessToken = data.token || data.accessToken || data.access_token;
    refreshToken = data.refreshToken || data.refresh_token;
    projectUuid = data.projectUuid || data.project_uuid;

    // Calculate expiration time
    const expiresIn = data.expiresIn || data.expires_in || 3600;
    tokenExpiresAt = Date.now() + (expiresIn * 1000);

    // Save to localStorage for persistence
    if (accessToken) {
        localStorage.setItem('embed_access_token', accessToken);
        // Also save as iframeToken for compatibility with auth.js
        localStorage.setItem('iframeToken', accessToken);
    }
    if (refreshToken) {
        localStorage.setItem('embed_refresh_token', refreshToken);
    }
    if (projectUuid) {
        localStorage.setItem('embed_project_uuid', projectUuid);
    }
    localStorage.setItem('embed_token_expires_at', String(tokenExpiresAt));

    console.log('[EmbedTokenManager] Token stored', { projectUuid, hasToken: true });

    return {
        success: true,
        accessToken,
        refreshToken,
        projectUuid,
        expiresIn,
    };
};

/**
 * Get current access token
 */
export const getAccessToken = () => {
    if (accessToken) return accessToken;
    return localStorage.getItem('embed_access_token');
};

/**
 * Get current refresh token
 */
export const getRefreshToken = () => {
    if (refreshToken) return refreshToken;
    return localStorage.getItem('embed_refresh_token');
};

/**
 * Get current project UUID
 */
export const getProjectUuid = () => {
    if (projectUuid) return projectUuid;
    return localStorage.getItem('embed_project_uuid');
};

/**
 * Check if token is expired or will expire soon
 */
export const isTokenExpired = (bufferSeconds = 60) => {
    const expiresAt = tokenExpiresAt || parseInt(localStorage.getItem('embed_token_expires_at') || '0', 10);
    return Date.now() >= (expiresAt - bufferSeconds * 1000);
};

// ── Other modules' tokens ───────────────────────────────────────────────
// The office hands a bundle (SET_TOKENS {tokens: {'zehn-docs': …, 'zehn-hr':
// …}}): docs' own token goes where SET_TOKEN's does, every other module's
// token is kept here, per tab, so docs' UI can ask that module for data as
// the same person (templates filled from its records, docs/template-data.md).
// Never sent to docs' own API.
const MODULE_TOKENS_KEY = 'embed_module_tokens';
export const DOCS_MODULE_CODE = 'zehn-docs';

const readModuleTokens = () => {
    try {
        return JSON.parse(sessionStorage.getItem(MODULE_TOKENS_KEY) || '{}') || {};
    } catch {
        return {};
    }
};

const writeModuleTokens = (tokens) => {
    try {
        if (Object.keys(tokens).length) sessionStorage.setItem(MODULE_TOKENS_KEY, JSON.stringify(tokens));
        else sessionStorage.removeItem(MODULE_TOKENS_KEY);
    } catch { /* storage blocked: the module just stays unavailable */ }
};

/**
 * Keep the other modules' tokens of a bundle. `replace` (a fresh SET_TOKENS):
 * a module missing from the bundle loses its token; a refresh keeps it.
 * docs' own entry is ignored here.
 */
export const storeModuleTokens = (bundle, { replace = false } = {}) => {
    const next = replace ? {} : readModuleTokens();
    for (const [code, token] of Object.entries(bundle || {})) {
        if (code !== DOCS_MODULE_CODE && token) next[code] = token;
    }
    writeModuleTokens(next);
};

/** One module's token (from a bundle or an ID-token exchange). */
export const storeModuleToken = (code, token) => {
    if (!code || code === DOCS_MODULE_CODE) return;
    const next = readModuleTokens();
    if (token) next[code] = token;
    else delete next[code];
    writeModuleTokens(next);
};

/** The person's token for another module, or null. */
export const getModuleToken = (code) => readModuleTokens()[code] || null;

/**
 * Clear all embed tokens
 */
export const clearEmbedTokens = () => {
    accessToken = null;
    refreshToken = null;
    projectUuid = null;
    tokenExpiresAt = null;

    localStorage.removeItem('embed_access_token');
    localStorage.removeItem('embed_refresh_token');
    localStorage.removeItem('embed_project_uuid');
    localStorage.removeItem('embed_token_expires_at');
    writeModuleTokens({});

    console.log('[EmbedTokenManager] Tokens cleared');
};

/**
 * Check if we have valid embed tokens
 */
export const hasValidEmbedTokens = () => {
    const token = getAccessToken();
    return Boolean(token && !isTokenExpired());
};

/**
 * Initialize from localStorage (for page reload scenarios)
 */
export const initFromStorage = () => {
    accessToken = localStorage.getItem('embed_access_token');
    refreshToken = localStorage.getItem('embed_refresh_token');
    projectUuid = localStorage.getItem('embed_project_uuid');
    tokenExpiresAt = parseInt(localStorage.getItem('embed_token_expires_at') || '0', 10) || null;

    if (accessToken) {
        console.log('[EmbedTokenManager] Initialized from storage', { projectUuid, hasToken: true });
    }
};

export default {
    storeAccessToken,
    storeModuleTokens,
    storeModuleToken,
    getModuleToken,
    getAccessToken,
    getRefreshToken,
    getProjectUuid,
    isTokenExpired,
    clearEmbedTokens,
    hasValidEmbedTokens,
    initFromStorage,
};
